import React, { createContext, useContext } from 'react'

import { getAllMyPets } from '../services/petService'
import { getPetsLocally } from '../services/petProfileService'
import { getServices } from '../services/serviceService'

import MODAL from '../utils/modals'
import { focus, splitServicesList, splitServiceName } from '../utils'
import { DEFAULT_ERROR } from '../utils/schedulingConstants'

import { useServiceScheduleContext, actionTypes } from './serviceScheduleContext'
import { serviceClickEvent } from '../utils/gtag'

export const petAndServicesContext = createContext()

// contexto de estado
export function PetAndServicesContextProvider ({ children }) {
  const { state, dispatch } = useServiceScheduleContext()

  const retrievePetsData = async (props) => {
    const { context } = props

    context.showLoader()
    const petsList =
      (context.firstName && (await getAllMyPets())) || getPetsLocally()

    dispatch({ type: actionTypes.UPDATE_STATE, payload: { petsData: petsList } })

    context.hideLoader()
  }

  const retrieveServiceData = async (props) => {
    const { context, router } = props

    context.showLoader()
    if (router?.query?.category || props?.category) {
      const serviceList = await getServices(
        (router?.query && router?.query?.category) || props?.category
          ? router?.query?.category
          : props?.category
            ? props?.category
            : undefined
      )

      if (serviceList === undefined || serviceList === null) {
        dispatch({ type: actionTypes.UPDATE_STATE, payload: { serviceData: [] } })
        context.hideLoader()
        return
      }

      const formattedServiceList = serviceList.map(
        ({ id, name, additional, category, package: isServicePackage, packageDiscount, packageQuantity, description }) => ({
          value: id,
          name,
          category,
          photo: undefined,
          additional,
          package: isServicePackage,
          packageDiscount,
          packageQuantity,
          description
        })
      )
      const {
        additionalServices,
        mainServices
      } = splitServicesList(formattedServiceList)

      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          serviceData: mainServices,
          additionalServiceData: additionalServices
        }
      })
    }
    context.hideLoader()
  }

  const onPetSelection = async (pet) => {
    const { scheduleBody, error } = state
    const newError = Object.assign({}, error)
    newError.pet_id = pet ? undefined : DEFAULT_ERROR.pet_id
    newError.unavailabilityPets = []

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: pet.id,
          professional_id: '',
          purchasedPackageId: undefined,
          isSchedulingFromPackage: false
        },
        selectedPurchasedPackage: undefined,
        error: newError,
        selectedPet: pet
      }
    })
  }

  const handleSelectedMyPet = async (pet, props) => {
    // verifica dados para atualizar cadastro do pet
    if (!pet.temperament || typeof pet.castrated !== 'boolean' || !pet.breed?.id || !pet.breed?.type || !pet.weight || !pet.gender) {
      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          modal: MODAL
            .INCOMPLETE_PET_FORM(
              pet,
              () => dispatch({
                type: actionTypes.UPDATE_STATE,
                payload: {
                  modal: MODAL.PET_SAVE_SUCCESS()
                }
              }))
        }
      })
    } else {
      // seleciona pet
      await onPetSelection(pet)
    }
  }

  const handleUnselectPet = () => {
    const { scheduleBody } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: undefined,
          professional_id: ''
        },
        selectedPet: undefined
      }
    })
  }

  const handleSelectedService = async (serviceId, focusRef, props) => {
    const { scheduleBody, error, rescheduleDataLoaded, changeStore, serviceData } = state
    const newError = Object.assign({}, error)

    newError.service_id = serviceId ? undefined : DEFAULT_ERROR.service_id
    const selectedService = serviceData.find(service => service.value === serviceId)
    newError.unavailabilityPets = []

    const changeServicePackage = selectedService?.package || scheduleBody.isServicePackage
    const additionalList = scheduleBody.additional_services
      ? changeServicePackage
        ? []
        : [...scheduleBody.additional_services]
      : []

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          service_id: serviceId,
          purchasedPackageId: undefined,
          isSchedulingFromPackage: false,
          samePetDate: changeStore ? false : undefined,
          isServicePackage: selectedService?.package || false,
          additional_services: additionalList
        },
        error: newError
      }
    })

    if (focusRef && (!props?.router?.query?.reschedule || !!rescheduleDataLoaded)) {
      focus(focusRef, 'smooth', true)
    }

    serviceClickEvent(props?.serviceName)
  }

  const handleToggleAdditional = async (serviceId) => {
    const { scheduleBody, changeStore, selectedPurchasedPackage } = state
    const additionalServices = (scheduleBody.additional_services || []).filter(Boolean)
    const isChecked = additionalServices?.find(service => service?.id === serviceId)

    const newAdditionalServices = !isChecked
      ? [...additionalServices, { id: serviceId, quantity: 1 }]
      : additionalServices.filter((service) => service?.id !== serviceId)

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          additional_services: newAdditionalServices,
          partner_id: selectedPurchasedPackage ? scheduleBody.partner_id : undefined,
          samePetDate: changeStore ? false : undefined
        }
      }
    })
  }

  const handleAdditionalQuantity = async (serviceId, additionalQuantity) => {
    const { scheduleBody } = state
    const additionalServices = scheduleBody.additional_services || []

    const current = additionalServices.find(service => service?.id === serviceId)
    const currentQuantity = current?.quantity || 0
    const newQuantity = currentQuantity + additionalQuantity

    if (newQuantity === 0) {
      handleToggleAdditional(serviceId)
      return
    }

    const updatedAdditionalServices = additionalServices.map(service => {
      if (service?.id === serviceId) {
        return { ...service, quantity: newQuantity }
      }
      return service
    })

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          additional_services: updatedAdditionalServices
        }
      }
    })
  }

  const calculateAvailableAdditionals = (additionalServices, currentUsages) => {
    return additionalServices
      .filter(item => (currentUsages.additional[item.id] || 0) < item.packageQuantity)
      .map(item => {
        const { id, ...rest } = item
        return { ...rest, value: id }
      })
  }

  const handleSelectedPurchasedPackage = (purchasedPackageSchedulingBody, availableAdditionals, purchasedPackageData) => {
    const { pet, service, professional, partner, additionalServices, currentUsages, expiresAt } = purchasedPackageData

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        selectedPet: pet,
        scheduleBody: {
          ...state.scheduleBody,
          ...purchasedPackageSchedulingBody
        },
        selectedPurchasedPackage: {
          pet,
          professional,
          partner,
          additionalServices,
          currentUsages,
          availableAdditionals,
          service,
          expiresAt
        },
        error: {
          ...state.error,
          professional_id: undefined,
          service_id: undefined,
          partner_id: undefined,
          pet_id: undefined
        }
      }
    })
  }

  const handlePurchasedPackageSelect = async (purchasedPackageData, props) => {
    const { router, context } = props
    const { pet, service, professional, partner, additionalServices, currentUsages } = purchasedPackageData
    const purchasedPackageSchedulingBody = {
      pet_id: pet?.id,
      purchasedPackageId: purchasedPackageData?.id,
      isSchedulingFromPackage: true,
      professional_id: professional?.id,
      partner_id: partner?.id,
      service_id: service?.linkedService?.id,
      isServicePackage: false
    }

    const availableAdditionals = calculateAvailableAdditionals(additionalServices, currentUsages)
    await handleSelectedMyPet(pet)
    await handleSelectedService(service?.linkedService?.id, undefined, {
      router,
      context,
      serviceName: splitServiceName(service.name)
    })

    handleSelectedPurchasedPackage(purchasedPackageSchedulingBody, availableAdditionals, purchasedPackageData)
  }

  const functions = {
    retrievePetsData,
    retrieveServiceData,
    handleSelectedMyPet,
    handleSelectedService,
    handleToggleAdditional,
    handleUnselectPet,
    handleAdditionalQuantity,
    onPetSelection,
    handlePurchasedPackageSelect
  }

  return (
    <petAndServicesContext.Provider value={{ functions }}>
      {children}
    </petAndServicesContext.Provider>
  )
}

// Hook para acessar o contexto
export function usePetAndServicesContext () {
  const context = useContext(petAndServicesContext)
  if (!context) {
    throw new Error('usePetAndServicesContext deve ser usado dentro de um Provider!')
  }

  const { functions } = context
  return { functions }
}
